export type LabelPlacement =
	| 'right'
	| 'left'
	| 'below'
	| 'above'
	| 'belowRight'
	| 'belowLeft'
	| 'aboveRight'
	| 'aboveLeft';

export interface Rect {
	x: number;
	y: number;
	width: number;
	height: number;
}

export interface Size {
	width: number;
	height: number;
}

export interface LabelledPoint {
	id: number;
	x: number;
	y: number;
	width: number;
	height: number;
	priority?: number;
}

interface LabelOffset {
	dx: number;
	dy: number;
	anchorX: number;
	anchorY: number;
}

export const TRUCK_CORE_RADIUS = 22;
export const LABEL_GAP = 6;
const SIDE_OFFSET = TRUCK_CORE_RADIUS + LABEL_GAP;
const CORNER_OFFSET = TRUCK_CORE_RADIUS;
// The truck number badge hangs off the top-right of the core.
const BADGE_CLEAR_OFFSET = 32;
// A label pushed off the map can still be panned to; one under another
// label can't be read at all.
const OUT_OF_VIEW_WEIGHT = 0.5;

// Where the label's anchor point (a fraction of its own box) sits relative
// to the marker centre, in order of preference.
export const LABEL_OFFSETS: Record<LabelPlacement, LabelOffset> = {
	right: { dx: SIDE_OFFSET, dy: 0, anchorX: 0, anchorY: 0.5 },
	left: { dx: -SIDE_OFFSET, dy: 0, anchorX: 1, anchorY: 0.5 },
	below: { dx: 0, dy: SIDE_OFFSET, anchorX: 0.5, anchorY: 0 },
	above: { dx: 0, dy: -SIDE_OFFSET, anchorX: 0.5, anchorY: 1 },
	belowRight: { dx: CORNER_OFFSET, dy: CORNER_OFFSET, anchorX: 0, anchorY: 0 },
	aboveRight: {
		dx: BADGE_CLEAR_OFFSET,
		dy: -CORNER_OFFSET,
		anchorX: 0,
		anchorY: 1,
	},
	belowLeft: { dx: -CORNER_OFFSET, dy: CORNER_OFFSET, anchorX: 1, anchorY: 0 },
	aboveLeft: { dx: -CORNER_OFFSET, dy: -CORNER_OFFSET, anchorX: 1, anchorY: 1 },
};

export const LABEL_PLACEMENTS = Object.keys(LABEL_OFFSETS) as LabelPlacement[];

export const labelRect = (
	point: Pick<LabelledPoint, 'x' | 'y' | 'width' | 'height'>,
	placement: LabelPlacement,
): Rect => {
	const { dx, dy, anchorX, anchorY } = LABEL_OFFSETS[placement];
	return {
		x: point.x + dx - anchorX * point.width,
		y: point.y + dy - anchorY * point.height,
		width: point.width,
		height: point.height,
	};
};

export const truckCoreRect = (x: number, y: number): Rect => ({
	x: x - TRUCK_CORE_RADIUS,
	y: y - TRUCK_CORE_RADIUS,
	width: TRUCK_CORE_RADIUS * 2,
	height: TRUCK_CORE_RADIUS * 2,
});

export const overlapArea = (a: Rect, b: Rect): number => {
	const width = Math.min(a.x + a.width, b.x + b.width) - Math.max(a.x, b.x);
	const height = Math.min(a.y + a.height, b.y + b.height) - Math.max(a.y, b.y);
	return width > 0 && height > 0 ? width * height : 0;
};

const outOfViewArea = (rect: Rect, viewport?: Size): number =>
	viewport
		? rect.width * rect.height - overlapArea(rect, { x: 0, y: 0, ...viewport })
		: 0;

/**
 * Picks a side for every label so none covers another label or a truck,
 * taking the points in priority order and keeping the default side whenever
 * it is free. A label with no clean side takes the one with the least
 * overlap.
 */
export const placeLabels = (
	points: LabelledPoint[],
	obstacles: Rect[],
	viewport?: Size,
): Record<number, LabelPlacement> => {
	const ordered = [...points].sort(
		(a, b) => (b.priority ?? 0) - (a.priority ?? 0) || a.y - b.y || a.x - b.x,
	);
	const taken = [...obstacles];
	const result: Record<number, LabelPlacement> = {};

	for (const point of ordered) {
		let best = LABEL_PLACEMENTS[0];
		let bestRect = labelRect(point, best);
		let bestScore = Infinity;

		for (const placement of LABEL_PLACEMENTS) {
			const rect = labelRect(point, placement);
			const score =
				taken.reduce((sum, other) => sum + overlapArea(rect, other), 0) +
				outOfViewArea(rect, viewport) * OUT_OF_VIEW_WEIGHT;
			if (score < bestScore) {
				best = placement;
				bestRect = rect;
				bestScore = score;
			}
			if (score === 0) break;
		}

		result[point.id] = best;
		taken.push(bestRect);
	}

	return result;
};

export const samePlacements = (
	a: Record<number, LabelPlacement>,
	b: Record<number, LabelPlacement>,
): boolean => {
	const keys = Object.keys(a);
	return (
		keys.length === Object.keys(b).length &&
		keys.every((key) => a[+key] === b[+key])
	);
};
